import React, {useEffect, useState} from "react";
import * as XLSX from "xlsx";
import {useTheme} from "@mui/material/styles";
import {
  AppBar,
  Card,
  Paper,
  Snackbar,
  Alert,
  Box,
  Tooltip,
  IconButton,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  TextField,
  FormControl,
  Tabs,
  Tab,
  Chip
} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {LastPage, FirstPage, NavigateBefore, NavigateNext} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Typography from "@mui/material/Typography";
import {GET} from "APIclient";
import {formatDate, formatDesignation} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const RATING_LEGEND = [
  {points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable"},
  {
    points: 2,
    rating: "Below Expectations",
    comment: "Performance fails to meet job requirement on frequent basis"
  },
  {
    points: 3,
    rating: "Meets Expectations",
    comment: "Performance is regularly competent & dependable"
  },
  {
    points: 4,
    rating: "Exceeds Expectations",
    comment: "Performance is routinely above expectation"
  },
  {points: 5, rating: "Outstanding", comment: "Performance is consistently superior"}
];

const ROW_HEIGHT = 36;
const colStyle = {display: "flex", alignItems: "center", minHeight: ROW_HEIGHT};
const COL = {num: 0.4, skill: 2.2, empScore: 1.2, empComment: 3.4, mgrScore: 1.2, mgrComment: 3.6};

const scoreSx = {
  height: ROW_HEIGHT,
  fontSize: "0.8rem",
  fontFamily: properties.fontFamily,
  "& .MuiSelect-select": {py: "6px", color: "#000 !important", fontFamily: properties.fontFamily},
  "&.Mui-disabled .MuiSelect-select": {
    color: "#000 !important",
    WebkitTextFillColor: "#000 !important"
  }
};

const commentSx = {
  "& .MuiOutlinedInput-root": {height: ROW_HEIGHT, alignItems: "center"},
  "& .MuiInputBase-input": {
    py: "6px",
    fontSize: "0.8rem",
    fontFamily: properties.fontFamily,
    color: "#000 !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "#000 !important",
    color: "#000 !important"
  }
};

function TabPanel({children, value, index}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box p={0.5}>{children}</Box>}
    </div>
  );
}

function CustomPagination({
  page,
  pageSize,
  totalRows,
  totalPages,
  onPageChange,
  onGoToLastPage,
  loading
}) {
  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 1,
        borderTop: "1px solid #e0e0e0"
      }}
    >
      <Typography variant="caption" sx={{color: "#666", fontFamily: properties.fontFamily}}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "No rows"}
      </Typography>
      <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
        <Typography
          variant="caption"
          sx={{mr: 2, color: "#666", fontFamily: properties.fontFamily}}
        >
          Page {page + 1} of {totalPages || 1}
        </Typography>
        <Tooltip title="First Page">
          <IconButton onClick={() => onPageChange(0)} disabled={page === 0 || loading} size="small">
            <FirstPage />
          </IconButton>
        </Tooltip>
        <Tooltip title="Previous Page">
          <IconButton
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>
        <Tooltip title="Next Page">
          <IconButton
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>
        <Tooltip title="Last Page">
          <IconButton
            onClick={onGoToLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || "#1976d2",
              color: "white",
              "&:hover": {backgroundColor: properties?.colors?.bgcolorbluethem || "#1565c0"},
              "&.Mui-disabled": {backgroundColor: "#e0e0e0", color: "#9e9e9e"}
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function RatingCell({value}) {
  if (value === null || value === undefined || value === "-") {
    return <span style={{color: "#9e9e9e"}}>—</span>;
  }
  const num = Number(value);
  let bg, color;
  if (num >= 4) {
    bg = "#e8f5e9";
    color = "#2e7d32";
  } else if (num >= 3) {
    bg = "#fff8e1";
    color = "#f57f17";
  } else {
    bg = "#ffebee";
    color = "#c62828";
  }
  return (
    <span
      style={{
        padding: "2px 10px",
        borderRadius: 12,
        background: bg,
        color,
        fontWeight: 600,
        fontSize: 12,
        fontFamily: properties.fontFamily
      }}
    >
      {num}
    </span>
  );
}

function QuestionnaireModal({open, onClose, empId, appraisalId, employeeName}) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) fetchQuestionnaire();
  }, [open]);

  const fetchQuestionnaire = async () => {
    try {
      setLoading(true);
      setError(null);
      setQuestions([]);
      const response = await GET(
        `admin/appraisal/questionnaire?empId=${empId}&appraisalId=${appraisalId}`
      );
      if (response.status === 200) {
        setQuestions(response.data?.data || []);
      } else if (response.status === 204) {
        setQuestions([]);
      } else {
        throw new Error("Failed to fetch questionnaire");
      }
    } catch (err) {
      setError("Failed to load questionnaire. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!questions.length) return;
    const exportData = questions.map((q, index) => ({
      "SR.No": index + 1,
      Question: q.question,
      Description: q.description || "—"
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet["!cols"] = [{wch: 6}, {wch: 50}, {wch: 50}];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Questionnaire");
    XLSX.writeFile(workbook, `Questionnaire_${empId}.xlsx`);
  };

  const handleClose = () => {
    setQuestions([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e0e0e0",
          pb: 1
        }}
      >
        <Box>
          <Typography
            sx={{fontWeight: "bold", fontSize: "16px", fontFamily: properties.fontFamily}}
          >
            Export Questionnaire
          </Typography>
          {employeeName && (
            <Typography sx={{fontSize: "12px", color: "#666", fontFamily: properties.fontFamily}}>
              {employeeName} &nbsp;|&nbsp; EMP ID: {empId}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{p: 0}}>
        {loading && (
          <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", py: 6}}>
            <CircularProgress size={32} />
            <Typography
              sx={{ml: 2, color: "#666", fontSize: "14px", fontFamily: properties.fontFamily}}
            >
              Loading questionnaire...
            </Typography>
          </Box>
        )}
        {!loading && error && (
          <Box sx={{p: 3}}>
            <Alert
              severity="error"
              action={
                <MDButton size="small" color="error" onClick={fetchQuestionnaire}>
                  Retry
                </MDButton>
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        {!loading && !error && questions.length === 0 && (
          <Box sx={{py: 6, textAlign: "center"}}>
            <Typography
              sx={{color: "#9e9e9e", fontSize: "14px", fontFamily: properties.fontFamily}}
            >
              No questionnaire data found for this employee.
            </Typography>
          </Box>
        )}
        {!loading && !error && questions.length > 0 && (
          <Box sx={{maxHeight: "400px", overflowY: "auto"}}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "50px 1fr 1fr",
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
                position: "sticky",
                top: 0,
                zIndex: 1
              }}
            >
              {["#", "Question", "Description"].map(h => (
                <Box
                  key={h}
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontWeight: "bold",
                    fontSize: "12px",
                    fontFamily: properties.fontFamily
                  }}
                >
                  {h}
                </Box>
              ))}
            </Box>
            {questions.map((q, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "50px 1fr 1fr",
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                  "&:hover": {backgroundColor: "#f0f4ff"}
                }}
              >
                <Box
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: "13px",
                    color: "#9e9e9e",
                    fontFamily: properties.fontFamily
                  }}
                >
                  {index + 1}
                </Box>
                <Box
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: "13px",
                    fontFamily: properties.fontFamily,
                    wordBreak: "break-word",
                    lineHeight: 1.6
                  }}
                >
                  {q.question || "—"}
                </Box>
                <Box
                  sx={{
                    py: 1.5,
                    px: 2,
                    fontSize: "13px",
                    color: "#555",
                    fontFamily: properties.fontFamily,
                    wordBreak: "break-word",
                    lineHeight: 1.6
                  }}
                >
                  {q.description || "—"}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {!loading && questions.length > 0 && (
        <DialogActions sx={{borderTop: "1px solid #e0e0e0", px: 2, py: 1}}>
          <Typography
            variant="caption"
            sx={{flex: 1, color: "#666", fontFamily: properties.fontFamily}}
          >
            {questions.length} question{questions.length !== 1 ? "s" : ""} found
          </Typography>
          <MDButton
            variant="gradient"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
          >
            Export to Excel
          </MDButton>
          <MDButton variant="outlined" color="secondary" onClick={handleClose}>
            Close
          </MDButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

function KRAFormModal({open, onClose, empId, appraisalId, employeeName}) {
  const [loading, setLoading] = useState(false);
  const [goalsData, setGoalsData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [appraisalData, setAppraisalData] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (open && empId && appraisalId) fetchData();
  }, [open, empId, appraisalId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [viewRes, goalsRes] = await Promise.all([
        GET(`manager/${appraisalId}/employee/${empId}/view`),
        GET(`manager/${appraisalId}/employee/${empId}/goals`)
      ]);
      if (viewRes.data?.data) {
        const data = viewRes.data.data;
        const nameParts = (data.employeeName || "").split(" ");
        setEmployeeData({
          empId: data?.empId || "",
          firstName: nameParts[0] || "",
          middleName: nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "",
          lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",
          gender: data.gender || "",
          dateOfJoining: data.dateOfJoining || "",
          totalExperience: data.totalExperience || ""
        });
        setViewData(data);
      }
      if (goalsRes.data?.data) setGoalsData(goalsRes.data.data);
    } catch (err) {
      console.error("Failed to load KRA form data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (goalsData && viewData) mergeSkillsData();
  }, [goalsData, viewData]);

  const mergeSkillsData = () => {
    const mergeSkills = (goalsSkills, viewSkills) =>
      (goalsSkills || []).map((goalSkill, index) => {
        const viewSkill = (viewSkills || [])[index] || {};
        return {
          skillName: goalSkill.skillSet,
          paFormId: goalSkill.paFormId,
          empScore: viewSkill.empScore || "",
          empComment: viewSkill.empComment || "",
          appraiserScore: viewSkill.appraiserScore || "",
          appraiserComment: viewSkill.appraiserComment || ""
        };
      });

    setAppraisalData({
      technicalPrimarySkills: mergeSkills(
        goalsData?.technicalPrimary,
        viewData?.technicalSkills?.primary
      ),
      technicalSecondarySkills: mergeSkills(
        goalsData?.technicalSecondary,
        viewData?.technicalSkills?.secondary
      ),
      competencyPrimarySkills: mergeSkills(
        goalsData?.competencyPrimary,
        viewData?.competencySkills?.primary
      ),
      competencySecondarySkills: mergeSkills(
        goalsData?.competencySecondary,
        viewData?.competencySkills?.secondary
      ),
      questionnaire: viewData?.questionnaire || []
    });
  };

  const handleClose = () => {
    setGoalsData(null);
    setViewData(null);
    setAppraisalData(null);
    setEmployeeData(null);
    setTabValue(0);
    onClose();
  };

  const handleExportExcel = () => {
    if (!appraisalData) return;
    const wb = XLSX.utils.book_new();

    const skillsToSheet = (skills, sheetName) => {
      if (!skills?.length) return;
      const data = skills.map((s, i) => ({
        "#": i + 1,
        "Skill Set": s.skillName,
        "Emp Score": s.empScore || "—",
        "Emp Comment": s.empComment || "—",
        "Manager Score": s.appraiserScore || "—",
        "Manager Comment": s.appraiserComment || "—"
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      ws["!cols"] = [{wch: 4}, {wch: 30}, {wch: 12}, {wch: 35}, {wch: 14}, {wch: 35}];
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    };

    skillsToSheet(appraisalData.technicalPrimarySkills, "Tech Primary");
    skillsToSheet(appraisalData.technicalSecondarySkills, "Tech Secondary");
    skillsToSheet(appraisalData.competencyPrimarySkills, "Comp Primary");
    skillsToSheet(appraisalData.competencySecondarySkills, "Comp Secondary");

    if (appraisalData.questionnaire?.length) {
      const qData = appraisalData.questionnaire.map((q, i) => ({
        "#": i + 1,
        Question: q.question || "—",
        Answer: q.description || "—"
      }));
      const qWs = XLSX.utils.json_to_sheet(qData);
      qWs["!cols"] = [{wch: 4}, {wch: 50}, {wch: 50}];
      XLSX.utils.book_append_sheet(wb, qWs, "Questionnaire");
    }

    XLSX.writeFile(wb, `KRA_Form_${empId}.xlsx`);
  };

  const InfoField = ({label, value, xs = 12, sm = 6, md = 3}) => (
    <Grid item xs={xs} sm={sm} md={md}>
      <MDBox
        p={0.5}
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          minHeight: "38px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center"
        }}
      >
        <Typography
          variant="caption"
          fontWeight="600"
          color="text.secondary"
          sx={{
            mb: 0.1,
            textTransform: "uppercase",
            fontSize: "0.6rem",
            fontFamily: properties.fontFamily
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight={500}
          sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
        >
          {value || "-"}
        </Typography>
      </MDBox>
    </Grid>
  );

  const renderSkillsTable = (skills, title) => {
    if (!skills?.length) {
      return (
        <Alert severity="warning" sx={{my: 0.5}}>
          <MDTypography variant="body2" sx={{fontFamily: properties.fontFamily}}>
            No {title} Added.
          </MDTypography>
        </Alert>
      );
    }

    const headerCellSx = {
      fontSize: "0.72rem",
      fontWeight: 700,
      color: "#444",
      lineHeight: 1.2,
      fontFamily: properties.fontFamily
    };

    return (
      <Box sx={{border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", mb: 1}}>
        <Grid container sx={{backgroundColor: "#f0f4f8", px: 0.75, py: 0.5}} alignItems="center">
          <Grid item xs={COL.num} sx={colStyle}>
            <Typography sx={headerCellSx}>#</Typography>
          </Grid>
          <Grid item xs={COL.skill} sx={colStyle}>
            <Typography sx={headerCellSx}>Skill Set</Typography>
          </Grid>
          <Grid item xs={COL.empScore} sx={{...colStyle, justifyContent: "center"}}>
            <Typography sx={{...headerCellSx, textAlign: "center"}}>Emp. Score</Typography>
          </Grid>
          <Grid item xs={COL.empComment} sx={colStyle}>
            <Typography sx={headerCellSx}>Emp. Comments</Typography>
          </Grid>
          <Grid item xs={COL.mgrScore} sx={{...colStyle, justifyContent: "center"}}>
            <Typography sx={{...headerCellSx, textAlign: "center"}}>Mgr Score</Typography>
          </Grid>
          <Grid item xs={COL.mgrComment} sx={colStyle}>
            <Typography sx={headerCellSx}>Manager Comment</Typography>
          </Grid>
        </Grid>

        {skills.map((skill, index) => (
          <Grid
            container
            key={index}
            alignItems="center"
            sx={{
              px: 0.75,
              py: 0.25,
              backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
              borderTop: "1px solid #eee",
              "&:hover": {backgroundColor: "#f5f9ff"},
              minHeight: ROW_HEIGHT
            }}
          >
            <Grid item xs={COL.num} sx={colStyle}>
              <Typography
                variant="body2"
                sx={{fontSize: "0.78rem", color: "#666", fontFamily: properties.fontFamily}}
              >
                {index + 1}
              </Typography>
            </Grid>
            <Grid item xs={COL.skill} sx={colStyle}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.8rem",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                  fontFamily: properties.fontFamily
                }}
              >
                {skill.skillName}
              </Typography>
            </Grid>
            <Grid item xs={COL.empScore} sx={{...colStyle, justifyContent: "center"}}>
              <FormControl size="small" disabled sx={{width: 68}}>
                <Select value={skill.empScore || ""} displayEmpty sx={scoreSx}>
                  <MenuItem value="" sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}>
                    -
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map(s => (
                    <MenuItem
                      key={s}
                      value={s}
                      sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={COL.empComment} sx={colStyle}>
              <TextField
                fullWidth
                value={skill.empComment || ""}
                disabled
                size="small"
                variant="outlined"
                placeholder="-"
                sx={commentSx}
              />
            </Grid>
            <Grid item xs={COL.mgrScore} sx={{...colStyle, justifyContent: "center"}}>
              <FormControl size="small" disabled sx={{width: 68}}>
                <Select value={skill.appraiserScore || ""} displayEmpty sx={scoreSx}>
                  <MenuItem value="" sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}>
                    -
                  </MenuItem>
                  {[1, 2, 3, 4, 5].map(s => (
                    <MenuItem
                      key={s}
                      value={s}
                      sx={{fontSize: "0.8rem", fontFamily: properties.fontFamily}}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={COL.mgrComment} sx={colStyle}>
              <TextField
                fullWidth
                value={skill.appraiserComment || ""}
                disabled
                size="small"
                variant="outlined"
                placeholder="-"
                sx={commentSx}
              />
            </Grid>
          </Grid>
        ))}
      </Box>
    );
  };

  const renderQuestionnaire = questionnaire => {
    if (!questionnaire?.length) {
      return (
        <Alert severity="warning">
          <MDTypography variant="body2" sx={{fontFamily: properties.fontFamily}}>
            No Questionnaire Added.
          </MDTypography>
        </Alert>
      );
    }
    return (
      <MDBox>
        {questionnaire.map((item, index) => (
          <MDBox key={index} mb={0.75}>
            <MDTypography
              variant="body2"
              mb={0.25}
              color="dark"
              fontWeight="medium"
              sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
            >
              {item.question}
            </MDTypography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={item.description || ""}
              disabled
              variant="outlined"
              size="small"
              placeholder="No answer provided"
              sx={{
                "& .MuiInputBase-input": {
                  color: "#000 !important",
                  fontSize: "0.82rem",
                  fontFamily: properties.fontFamily
                },
                "& .MuiInputBase-input.Mui-disabled": {WebkitTextFillColor: "#000 !important"}
              }}
            />
          </MDBox>
        ))}
      </MDBox>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{sx: {height: "90vh"}}}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e0e0e0",
          pb: 1,
          pt: 1
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            sx={{fontWeight: "bold", fontSize: "1rem", fontFamily: properties.fontFamily}}
          >
            Employee KRA Appraisal Form
          </Typography>
          <Chip
            label="Admin View Only"
            size="small"
            sx={{
              backgroundColor: "rgba(25,118,210,0.1)",
              color: "#1976d2",
              fontWeight: 700,
              fontSize: "0.7rem",
              fontFamily: properties.fontFamily,
              border: "1px solid rgba(25,118,210,0.3)"
            }}
          />
          {employeeName && (
            <Typography
              sx={{fontSize: "0.78rem", color: "#666", fontFamily: properties.fontFamily}}
            >
              &nbsp;|&nbsp; {employeeName} &nbsp;|&nbsp; EMP ID: {empId}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{p: 1.5, overflowY: "auto"}}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px"
            }}
          >
            <CircularProgress size={32} />
            <Typography
              sx={{ml: 2, color: "#666", fontSize: "14px", fontFamily: properties.fontFamily}}
            >
              Loading KRA form...
            </Typography>
          </Box>
        ) : (
          <MDBox>
            <Alert
              severity="info"
              sx={{mb: 0.75, fontSize: "0.82rem", fontFamily: properties.fontFamily}}
            >
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
              >
                You are viewing this appraisal form as an Admin. No changes can be made from this
                view.
              </Typography>
            </Alert>

            <Card variant="outlined" sx={{mb: 0.75}}>
              <MDBox
                px={0.75}
                pt={0.5}
                pb={0.25}
                sx={{
                  background: "linear-gradient(135deg, #e2e2e2 0%, #ffffffc9 100%)",
                  borderBottom: "1px solid #e0e0e0"
                }}
              >
                <MDTypography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{fontSize: "0.85rem", fontFamily: properties.fontFamily}}
                >
                  Official Details
                </MDTypography>
              </MDBox>
              <MDBox px={0.75} py={0.5}>
                <Grid container spacing={0.5}>
                  <InfoField label="Employee ID" value={employeeData?.empId} />
                  <InfoField label="First Name" value={employeeData?.firstName} />
                  <InfoField label="Middle Name" value={employeeData?.middleName} />
                  <InfoField label="Last Name" value={employeeData?.lastName} />
                  <InfoField label="Gender" value={employeeData?.gender} />
                  <InfoField
                    label="Date of Joining"
                    value={
                      employeeData?.dateOfJoining
                        ? new Date(employeeData.dateOfJoining).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric"
                          })
                        : "-"
                    }
                  />
                  <InfoField
                    label="Total Experience (Years)"
                    value={employeeData?.totalExperience}
                  />
                </Grid>
              </MDBox>
            </Card>

            <Card variant="outlined" sx={{mb: 0.75}}>
              <MDBox
                px={0.75}
                pt={0.5}
                pb={0.25}
                sx={{backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0"}}
              >
                <MDTypography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{fontSize: "0.85rem", fontFamily: properties.fontFamily}}
                >
                  Performance Evaluation Form
                </MDTypography>
              </MDBox>
              <MDBox px={0.75} py={0.5}>
                <MDTypography
                  variant="body2"
                  mb={0.5}
                  color="black"
                  sx={{fontSize: "0.78rem", fontFamily: properties.fontFamily}}
                >
                  Use the following ratings for consistency.
                </MDTypography>
                <Grid
                  container
                  sx={{
                    backgroundColor: "#f0f4f8",
                    px: 0.5,
                    py: 0.3,
                    borderRadius: "4px 4px 0 0",
                    border: "1px solid #ddd"
                  }}
                >
                  <Grid item xs={1}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Points
                    </Typography>
                  </Grid>
                  <Grid item xs={2.5}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Ratings
                    </Typography>
                  </Grid>
                  <Grid item xs={8.5}>
                    <Typography
                      sx={{fontSize: "0.72rem", fontWeight: 700, fontFamily: properties.fontFamily}}
                    >
                      Comments
                    </Typography>
                  </Grid>
                </Grid>
                {RATING_LEGEND.map((row, index) => (
                  <Grid
                    container
                    key={row.points}
                    sx={{
                      px: 0.5,
                      py: 0.2,
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                      border: "1px solid #ddd",
                      borderTop: "none",
                      borderRadius: index === RATING_LEGEND.length - 1 ? "0 0 4px 4px" : 0
                    }}
                    alignItems="center"
                  >
                    <Grid item xs={1}>
                      <Typography sx={{fontSize: "0.78rem", fontFamily: properties.fontFamily}}>
                        {row.points}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#222",
                          fontFamily: properties.fontFamily
                        }}
                      >
                        {row.rating}
                      </Typography>
                    </Grid>
                    <Grid item xs={8.5}>
                      <Typography
                        sx={{fontSize: "0.78rem", color: "#333", fontFamily: properties.fontFamily}}
                      >
                        {row.comment}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </MDBox>
            </Card>

            <Card variant="outlined" sx={{mb: 0.75}}>
              <Box sx={{borderBottom: "1px solid #ddd"}}>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => setTabValue(v)}
                  textColor="info"
                  indicatorColor="info"
                  variant="fullWidth"
                  sx={{
                    minHeight: 38,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                      fontFamily: properties.fontFamily,
                      minHeight: 38,
                      py: 0
                    },
                    "& .Mui-selected": {color: "#2196f3 !important"}
                  }}
                >
                  <Tab label="Technical Skills" />
                  <Tab label="Performance Competency" />
                  <Tab label="Questionnaire" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Technical Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalPrimarySkills,
                  "Technical Primary Skills"
                )}
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Technical Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.technicalSecondarySkills,
                  "Technical Secondary Skills"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Competency Primary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencyPrimarySkills,
                  "Competency Primary Skills"
                )}
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.25}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Competency Secondary Skills
                </MDTypography>
                {renderSkillsTable(
                  appraisalData?.competencySecondarySkills,
                  "Competency Secondary Skills"
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <MDTypography
                  variant="subtitle2"
                  fontWeight="bold"
                  mb={0.5}
                  sx={{fontSize: "0.82rem", fontFamily: properties.fontFamily}}
                >
                  Questionnaire
                </MDTypography>
                {renderQuestionnaire(appraisalData?.questionnaire)}
              </TabPanel>
            </Card>
          </MDBox>
        )}
      </DialogContent>

      {!loading && appraisalData && (
        <DialogActions sx={{borderTop: "1px solid #e0e0e0", px: 2, py: 1}}>
          <MDButton
            variant="gradient"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportExcel}
            disabled={downloadLoading}
            sx={{fontFamily: properties.fontFamily}}
          >
            Export to Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            sx={{fontFamily: properties.fontFamily}}
          >
            Close
          </MDButton>
        </DialogActions>
      )}
    </Dialog>
  );
}

const PerformanceAppraisalReport = () => {
  const theme = useTheme();

  const [appraisalList, setAppraisalList] = useState([]);
  const [filteredAppraisalList, setFilteredAppraisalList] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchEmpId, setSearchEmpId] = useState("");
  const [searchEmpName, setSearchEmpName] = useState("");
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [questionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);
  const [kraModalOpen, setKraModalOpen] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [selectedAprId, setSelectedAprId] = useState(null);
  const [selectedEmpName, setSelectedEmpName] = useState("");

  const options = ["Export Questionnaire", "Download KRA Form"];

  const columns = [
    {field: "id", headerName: "#", width: 30, align: "left", headerAlign: "left"},
    {field: "empId", headerName: "EMP ID", width: 70, align: "left", headerAlign: "left"},
    {
      field: "employeeName",
      headerName: "Employee Name",
      width: 170,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "evaluationPeriod",
      headerName: "Evaluation Period",
      width: 180,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "joinDate",
      headerName: "Joining Date",
      width: 130,
      align: "left",
      headerAlign: "left",
      renderCell: params => params.value || "N/A"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "selfRatingAvg",
      headerName: "Self Rating Avg.",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: params => <RatingCell value={params.value} />
    },
    {
      field: "managerRatingAvg",
      headerName: "Manager Rating Avg.",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: params => <RatingCell value={params.value} />
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      align: "left",
      headerAlign: "left",
      renderCell: params => (
        <div style={{textAlign: "left"}}>
          <Select
            variant="standard"
            key={`menu-${params.row.empId}`}
            value=""
            onChange={e => handleActionSelect(params.row, e.target.value)}
            displayEmpty
            style={{width: "1px"}}
            renderValue={() => (
              <span style={{display: "flex", alignItems: "center", justifyContent: "flex-start"}}>
                <MoreVertIcon style={{marginRight: "8px"}} />
              </span>
            )}
            MenuProps={{PaperProps: {style: {textAlign: "left"}}}}
          >
            {options.map((option, index) => (
              <MenuItem
                key={index}
                value={option}
                style={{justifyContent: "flex-start", fontFamily: properties.fontFamily}}
              >
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
      )
    }
  ];

  const fetchAppraisalList = async (pageNum, pageSz, empId, empName) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pageNum,
        size: pageSz,
        empId: empId || "",
        empName: empName || ""
      }).toString();
      const response = await GET(`admin/appraisal/list?${queryParams}`);
      if (response.status === 200) {
        const d = response.data?.data;
        const content = d?.paginatedData || [];
        const rows = content.map((item, index) => ({
          id: pageNum * pageSz + index + 1,
          empId: item.empId,
          appraisalId: item.appraisalId,
          employeeName: [item.firstName, item.lastName].filter(Boolean).join(" "),
          evaluationPeriod: formatPeriod(item.evaluationStartDate, item.evaluationEndDate),
          joinDate: item.joinDate || "N/A",
          designation: formatDesignation(item.designation) || item.designation || "N/A",
          selfRatingAvg: item.selfRatingAvg ?? null,
          managerRatingAvg: item.managerRatingAvg ?? null
        }));
        setAppraisalList(rows);
        setFilteredAppraisalList(rows);
        setTotalRows(d?.totalItems || 0);
        setTotalPages(d?.totalPages || 0);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      setSnackMessage("Failed to fetch appraisal list.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppraisalList(page, pageSize, searchEmpId, searchEmpName);
  }, [page, pageSize]);

  const formatPeriod = (start, end) => {
    if (!start || !end) return "N/A";
    const fmt = d => new Date(d).toLocaleDateString("en-IN", {month: "short", year: "numeric"});
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleActionSelect = (row, selectedOption) => {
    setSelectedEmpId(row.empId);
    setSelectedAprId(row.appraisalId);
    setSelectedEmpName(row.employeeName);
    if (selectedOption === "Export Questionnaire") {
      setQuestionnaireModalOpen(true);
    } else if (selectedOption === "Download KRA Form") {
      setKraModalOpen(true);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchAppraisalList(0, pageSize, searchEmpId, searchEmpName);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter") handleSearch();
  };

  const handlePaginationChange = paginationModel => {
    setPage(paginationModel.page);
    setPageSize(paginationModel.pageSize);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    fetchAppraisalList(newPage, pageSize, searchEmpId, searchEmpName);
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPage = totalPages - 1;
      setPage(lastPage);
      await fetchAppraisalList(lastPage, pageSize, searchEmpId, searchEmpName);
      setTimeout(() => window.scrollTo(0, 0), 0);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      const queryParams = new URLSearchParams({
        page: 0,
        size: totalRows || 10000,
        empId: searchEmpId || "",
        empName: searchEmpName || ""
      }).toString();
      const response = await GET(`admin/appraisal/list?${queryParams}`);
      if (response.status === 200) {
        const d = response.data?.data;
        const content = d?.paginatedData || [];
        const exportData = content.map((item, index) => ({
          "SR.No": index + 1,
          "Employee ID": item.empId,
          "Employee Name": [item.firstName, item.lastName].filter(Boolean).join(" "),
          "Evaluation Period": formatPeriod(item.evaluationStartDate, item.evaluationEndDate),
          "Joining Date": item.joinDate || "N/A",
          Designation: formatDesignation(item.designation) || item.designation || "N/A",
          "Self Rating Avg.": item.selfRatingAvg ?? "—",
          "Manager Rating Avg.": item.managerRatingAvg ?? "—"
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        worksheet["!cols"] = [
          {wch: 6},
          {wch: 15},
          {wch: 25},
          {wch: 25},
          {wch: 15},
          {wch: 30},
          {wch: 18},
          {wch: 20}
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Appraisal List");
        XLSX.writeFile(workbook, "Appraisal_List.xlsx");
      } else {
        setSnackMessage("Failed to download data.");
        setSnackColor("error");
        setSnackOpen(true);
      }
    } catch (error) {
      setSnackMessage("Failed to download data.");
      setSnackColor("error");
      setSnackOpen(true);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} px={1} pb={2}>
              <MDBox
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(13, 1fr)",
                  gap: 1,
                  alignItems: "center",
                  whiteSpace: "nowrap"
                }}
              >
                <Typography
                  sx={{
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Performance Evaluation Rating Report
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 3", ml: 1}}
                  value={searchEmpId}
                  onChange={e => setSearchEmpId(e.target.value)}
                  label="Employee ID"
                  onKeyDown={handleKeyDown}
                />
                <MDInput
                  sx={{gridColumn: "span 3", ml: 1}}
                  value={searchEmpName}
                  onChange={e => setSearchEmpName(e.target.value)}
                  label="Employee Name"
                  onKeyDown={handleKeyDown}
                />
                <MDButton
                  sx={{gridColumn: "span 2", ml: 1, background: properties.colors.bgcolorbluethem}}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{gridColumn: "span 2", ml: 1}}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                  disabled={downloadLoading}
                >
                  {downloadLoading ? "Downloading..." : "Download"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>

        <MDBox>
          <Paper>
            <DataGrid
              rows={filteredAppraisalList}
              columns={columns}
              paginationMode="server"
              rowCount={totalRows}
              pageSizeOptions={[10, 20, 50]}
              paginationModel={{page, pageSize}}
              onPaginationModelChange={handlePaginationChange}
              loading={loading}
              hideFooter
              sx={{
                fontFamily: properties.fontFamily,
                fontSize: "14px",
                fontWeight: 400,
                "& .MuiDataGrid-cell": {textAlign: "left"},
                "& .MuiDataGrid-columnHeaderTitle": {textAlign: "left"},
                "& .MuiDataGrid-columnHeader": {textAlign: "left"},
                "& .MuiDataGrid-footerContainer": {justifyContent: "flex-start"},
                "& .MuiDataGrid-scrollbar": {width: "10px !important"},
                height: "450px"
              }}
            />
            <CustomPagination
              page={page}
              pageSize={pageSize}
              totalRows={totalRows}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onGoToLastPage={handleGoToLastPage}
              loading={loading}
            />
          </Paper>
        </MDBox>

        <QuestionnaireModal
          open={questionnaireModalOpen}
          onClose={() => setQuestionnaireModalOpen(false)}
          empId={selectedEmpId}
          appraisalId={selectedAprId}
          employeeName={selectedEmpName}
        />

        <KRAFormModal
          open={kraModalOpen}
          onClose={() => setKraModalOpen(false)}
          empId={selectedEmpId}
          appraisalId={selectedAprId}
          employeeName={selectedEmpName}
        />

        <Snackbar
          open={snackOpen}
          autoHideDuration={3000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
        >
          <Alert onClose={() => setSnackOpen(false)} severity={snackColor}>
            {snackMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default PerformanceAppraisalReport;
